/** The presentation id + PIN pair, and the links that carry them.
 *
 * The id names a relay and the PIN authorises joining it; neither admits a remote on its
 * own. A QR link additionally carries the relay's long `join_secret`, which is a single
 * unguessable capability — that is why a scanned link joins in one step while a typed
 * join always asks for both halves.
 */

// Mirrors backend/presentation/presentation_ids.py — no 0, 1, I or O, because these codes
// are read off a television across a room. That leaves thirty-two characters exactly.
export const PUBLIC_ID_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ'
export const PUBLIC_ID_LENGTH = 6

export function normalizePublicId(value: string): string {
  return value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, PUBLIC_ID_LENGTH)
}

export function isPublicId(value: string): boolean {
  return (
    value.length === PUBLIC_ID_LENGTH
    && [...value].every((character) => PUBLIC_ID_ALPHABET.includes(character))
  )
}

/** Does this address belong to the account-free remote shell? (`/remote`, `/remote/<id>`) */
export function isRemotePath(pathname: string): boolean {
  return /^\/remote(\/|$)/.test(pathname)
}

/** Does this address belong to the watch-along viewer? (`/watch`, `/watch/<id>`) */
export function isWatchPath(pathname: string): boolean {
  return /^\/watch(\/|$)/.test(pathname)
}

export function remoteUrl(publicId = '', origin = window.location.origin): string {
  return publicId ? `${origin}/remote/${publicId}` : `${origin}/remote`
}

/** The one-scan remote link: the id addresses the relay, the fragment admits the remote. */
export function remoteJoinUrl(
  publicId: string,
  joinSecret: string,
  origin = window.location.origin,
): string {
  return `${remoteUrl(publicId, origin)}#join=${encodeURIComponent(joinSecret)}`
}

export function watchAddress(publicId = '', origin = window.location.origin): string {
  return publicId ? `${origin}/watch/${publicId}` : `${origin}/watch`
}

/** The watch-along link — deliberately carrying no key at all.
 *
 * It addresses the relay and nothing else. A watcher that opens it mints its own
 * keypair and waits for the presenter to seal the gallery grant to that key, so a
 * photographed or forwarded link admits nobody the presenter did not approve, and it
 * stops meaning anything once the presentation ends.
 */
export function watchUrl(
  publicId: string,
  joinSecret: string,
  origin = window.location.origin,
): string {
  return `${watchAddress(publicId, origin)}#join=${encodeURIComponent(joinSecret)}`
}
